import React from 'react';
import {
  TouchableWithoutFeedback,
  ScrollView,
  StyleSheet,
} from 'react-native';
import {
  Box,
  Inline,
  IBoxProps,
  BoxSizeOptions,
  PaddingSize,
} from '@styleseat/ui';
import { colors } from '@styleseat/brand';
import { HeaderText } from '..';

const NOOP = () => { };

export type TabNavigationProps = {
  /** called when this component mounts for the first time */
  onMount?: () => void;
  /** Array of strings for tabs */
  tabs: Array<string>;
  /** Tab to set to starting active state */
  activeTab: number;
  /** The color of the border below the active tab */
  activeColor?: string;
  /** Function to call on tab click */
  onSelectedTabChange: (newIndex: number) => void;
  /** If true tab navigation will scroll horizontally to the right */
  shouldScroll?: boolean;
  /** Space between individual tabs */
  space?: PaddingSize;
  /** Space around all tabs */
  paddingX?: BoxSizeOptions;
  /** Space above tabs */
  paddingTop?: BoxSizeOptions;
  /** True to center content, otherwise it will be left-aligned */
  centerContent?: boolean;
  /** Additional props to add to the `Box` components that make up each tab */
  tabProps?: Partial<IBoxProps>;
  renderBadge: (tabLabel: string) => React.ReactNode;
};

/**
 * A general purpose tab navigation component.
 * Takes an array of strings to render as navigation
 * tabs.
 *
 *
 * @example AngularJS
 *   tab-navigation(
 *     tabs="tabHeaders"
 *     on-click="setFilter"
 *     active-tab="0"
 *   )
 *
 * @example React
 * <TabNavigation
 *   tabs={tabHeaders}
 *   onClick={setFilter}
 *   activeTab={0}
 * >
 * </TabNavigation>
 *
 *
 */
export default class TabNavigation extends React.PureComponent<TabNavigationProps> {
  static defaultProps: Partial<TabNavigationProps> = {
    // This component is used in Angular land where required-ness of props is not supported
    // eslint-disable-next-line react/default-props-match-prop-types
    renderBadge: undefined,
    // eslint-disable-next-line react/default-props-match-prop-types
    onSelectedTabChange: () => { },
    shouldScroll: false,
    onMount: NOOP,
    space: 24,
    paddingX: 0,
    centerContent: false,
    tabProps: {},
    paddingTop: 0,
    activeColor: colors.blackInBlack,
  };

  elTabNavigation = null;

  componentDidMount() {
    this.props.onMount();
    this.scrollToActive();
  }

  componentDidUpdate() {
    this.scrollToActive();
  }

  setTabNavigationElement = elTabNavigation => {
    this.elTabNavigation = elTabNavigation;
  };

  scrollToActive = () => {
    const {
      shouldScroll,
      activeTab,
    } = this.props;
    if (this.elTabNavigation !== null && shouldScroll) {
      this.elTabNavigation.querySelector(`[data-tab="${activeTab}"]`)
        .scrollIntoView();
    }
  };

  /**
   * Sets tab to 'active' state
   * @param  {Event} event
   */
  setActiveTabHandler = index => {
    this.props.onSelectedTabChange(index);
  };

  render() {
    const {
      tabs,
      activeTab,
      activeColor,
      tabProps,
      centerContent,
      paddingX,
      paddingTop,
      space,
      shouldScroll,
      renderBadge,
    } = this.props;

    if (!tabs) {
      return null;
    }

    /*
     This needs to be a div because we need a ref to scroll into view
     Attempts to make this work with <View> elements did not work!
    */
    return (
      <div
        style={{
          borderStyle: 'solid',
          borderWidth: 0,
          borderBottomWidth: StyleSheet.hairlineWidth,
          borderBottomColor: colors.lightGrey,
          width: '100%',
          display: 'flex',
          alignItems: 'stretch',
        }}
        ref={this.setTabNavigationElement}
      >
        <ScrollView
          contentContainerStyle={centerContent ? {
            margin: 'auto',
            flex: shouldScroll ? undefined : 1,
          } : {
            flex: shouldScroll ? undefined : 1,
          }}
          horizontal
        >
          <Inline
            paddingX={paddingX}
            paddingTop={paddingTop}
            space={space}
            justifyContent={centerContent ? 'center' : 'flex-start'}
            flex={shouldScroll ? undefined : 1}
          >
            {tabs.map((tab, index) => (
              <TouchableWithoutFeedback
                key={tab}
                onPress={() => this.setActiveTabHandler(index)}
              >
                <Box
                  justifyContent="center"
                  alignItems="center"
                  borderStyle="solid"
                  borderBottomWidth={3}
                  borderBottomColor={activeTab === index ? activeColor : colors.transparent}
                  data-tab={index}
                  testID={`tab-navigation-${tab}`}
                  padding={4}
                  {...tabProps}
                >
                  {typeof renderBadge === 'function' && renderBadge(tab)}
                  <HeaderText
                    color={activeTab === index ? colors.blackInBlack : colors.ash}
                    level={4}
                  >
                    {tab}
                  </HeaderText>
                </Box>
              </TouchableWithoutFeedback>
            ))}
          </Inline>
        </ScrollView>
      </div>
    );
  }
}
